// Pagina NOTE - lista, ricerca, creazione/modifica + collegamento promemoria
import { useEffect, useState, useMemo } from 'react';
import { Header } from '@/components/layout/Header';
import { PageContainer } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Label, Field } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, Badge } from '@/components/ui/Misc';
import { useNotesStore } from '@/store/notesStore';
import { useRemindersStore } from '@/store/remindersStore';
import { toast } from '@/hooks/useToast';
import { useI18n, useWeekdays, useWeekdayPresets } from '@/i18n';
import { NOTE_COLORS, NOTE_COLOR_KEYS } from '@/utils/constants';
import { cn, formatDate, nowISO } from '@/utils';
import type { Note, NoteColor } from '@/types';
import { FileText, Plus, Pin, Search, Archive, Trash2, Bell, BellRing, Clock } from 'lucide-react';

export function NotesPage() {
  const { t } = useI18n();
  const { notes, search, setSearch, load, create, update, remove, togglePin, toggleArchive } = useNotesStore();
  const { reminders } = useRemindersStore();
  const [editing, setEditing] = useState<Note | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    load();
  }, [load]);

  function hasActiveReminder(note: Note): boolean {
    if (!note.reminder_id) return false;
    const r = reminders.find((x) => x.id === note.reminder_id);
    return !!r && r.enabled;
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return notes
      .filter((n) => (showArchived ? n.is_archived : !n.is_archived))
      .filter((n) => !q || n.title.toLowerCase().includes(q) || n.content.toLowerCase().includes(q) || n.tags.some((tg) => tg.toLowerCase().includes(q)));
  }, [notes, search, showArchived]);

  const pinned = filtered.filter((n) => n.is_pinned);
  const others = filtered.filter((n) => !n.is_pinned);

  return (
    <>
      <Header
        title={t('notes.title')}
        subtitle={t('notes.subtitle', { count: notes.filter((n) => !n.is_archived).length, archived: notes.filter((n) => n.is_archived).length })}
        icon={<div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center"><FileText size={18} className="text-amber-600 dark:text-amber-400" /></div>}
        actions={
          <>
            <button
              onClick={() => setShowArchived((v) => !v)}
              className={cn('p-2 rounded-lg transition-colors', showArchived ? 'text-brand-600 bg-brand-50 dark:bg-brand-900/30' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800')}
              title={t('notes.empty_archived')}
            >
              <Archive size={18} />
            </button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={16} /> {t('notes.new')}
            </Button>
          </>
        }
      />

      <PageContainer>
        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t('notes.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={<FileText size={48} strokeWidth={1.5} />}
            title={showArchived ? t('notes.empty_archived') : t('notes.empty_title')}
            description={showArchived ? t('notes.empty_archived_desc') : t('notes.empty_desc')}
            action={!showArchived && <Button onClick={() => setShowCreate(true)}><Plus size={16} /> {t('notes.new')}</Button>}
          />
        ) : (
          <div className="space-y-5">
            {pinned.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Pin size={12} /> {t('notes.pinned')}
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {pinned.map((n) => (
                    <NoteCard key={n.id} note={n} hasActiveReminder={hasActiveReminder(n)} onClick={() => setEditing(n)} onPin={() => togglePin(n.id)} onArchive={() => toggleArchive(n.id)} onDelete={() => remove(n.id)} />
                  ))}
                </div>
              </section>
            )}
            {others.length > 0 && (
              <section>
                {pinned.length > 0 && <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('notes.others')}</h2>}
                <div className="grid grid-cols-2 gap-3">
                  {others.map((n) => (
                    <NoteCard key={n.id} note={n} hasActiveReminder={hasActiveReminder(n)} onClick={() => setEditing(n)} onPin={() => togglePin(n.id)} onArchive={() => toggleArchive(n.id)} onDelete={() => remove(n.id)} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </PageContainer>

      {showCreate && (
        <NoteEditor
          onClose={() => setShowCreate(false)}
          onSave={async (data) => {
            await create(data);
            toast.success(t('notes.created'));
            setShowCreate(false);
          }}
        />
      )}

      {editing && (
        <NoteEditor
          note={editing}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            await update(editing.id, data);
            toast.success(t('notes.updated'));
            setEditing(null);
          }}
          onDelete={async () => {
            await remove(editing.id);
            toast.success(t('notes.deleted'));
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------
// Card nota
// ------------------------------------------------------------
function NoteCard({
  note,
  hasActiveReminder,
  onClick,
  onPin,
  onArchive,
  onDelete,
}: {
  note: Note;
  hasActiveReminder: boolean;
  onClick: () => void;
  onPin: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const color = NOTE_COLORS[note.color];
  return (
    <div
      className={cn(
        'relative rounded-2xl border p-3 transition-all cursor-pointer hover:shadow-md animate-scale-in',
        color.bg,
        color.border,
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="font-semibold text-sm text-slate-900 dark:text-white line-clamp-2 flex-1">
          {note.title || t('notes.untitled')}
        </h3>
        <div className="flex items-center gap-0.5 shrink-0">
          {hasActiveReminder && (
            <span className="p-1 text-rose-500" title={t('notes.reminder_section')}>
              <BellRing size={12} />
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onPin(); }}
            className={cn('p-1 rounded-md transition-colors', note.is_pinned ? 'text-amber-500' : 'text-slate-300 hover:text-slate-500')}
          >
            <Pin size={14} fill={note.is_pinned ? 'currentColor' : 'none'} />
          </button>
        </div>
      </div>
      {note.content && (
        <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-4 whitespace-pre-wrap">{note.content}</p>
      )}
      {note.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {note.tags.slice(0, 3).map((tg) => (
            <Badge key={tg} className="bg-slate-200/60 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 text-[10px]">#{tg}</Badge>
          ))}
          {note.tags.length > 3 && <Badge className="text-[10px]">+{note.tags.length - 3}</Badge>}
        </div>
      )}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-black/5 dark:border-white/5">
        <span className="text-[10px] text-slate-400">{formatDate(note.updated_at)}</span>
        <div className="flex gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onArchive(); }}
            className="p-1 rounded text-slate-300 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <Archive size={12} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1 rounded text-slate-300 hover:text-rose-500"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Editor nota (modal) - con sezione promemoria
// ------------------------------------------------------------
function NoteEditor({
  note,
  onClose,
  onSave,
  onDelete,
}: {
  note?: Note;
  onClose: () => void;
  onSave: (data: Partial<Note>) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const { t } = useI18n();
  const weekdays = useWeekdays();
  const presets = useWeekdayPresets();
  const { reminders, create: createReminder, update: updateReminder, remove: removeReminder } = useRemindersStore();
  const [title, setTitle] = useState(note?.title ?? '');
  const [content, setContent] = useState(note?.content ?? '');
  const [color, setColor] = useState<NoteColor>(note?.color ?? 'default');
  const [tagsInput, setTagsInput] = useState(note?.tags.join(', ') ?? '');
  const [saving, setSaving] = useState(false);

  // Reminder collegato (se esiste)
  const linkedReminder = note?.reminder_id ? reminders.find((r) => r.id === note.reminder_id) : null;
  const [showReminderSection, setShowReminderSection] = useState(!!linkedReminder);
  const [reminderTime, setReminderTime] = useState(linkedReminder?.time ?? '09:00');
  const [reminderDays, setReminderDays] = useState<number[]>(linkedReminder?.days_of_week ?? [1, 2, 3, 4, 5]);
  const [reminderEnabled, setReminderEnabled] = useState(linkedReminder?.enabled ?? true);

  function toggleDay(day: number) {
    setReminderDays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const tags = tagsInput
        .split(',')
        .map((tg) => tg.trim().replace(/^#/, ''))
        .filter(Boolean);

      let reminderId = note?.reminder_id ?? null;

      // Gestione reminder
      if (showReminderSection) {
        if (reminderId && linkedReminder) {
          await updateReminder(reminderId, {
            title: `${t('notes.title')}: ${title || t('notes.untitled')}`,
            body: content.slice(0, 200),
            time: reminderTime,
            days_of_week: reminderDays,
            enabled: reminderEnabled,
          });
        } else {
          const r = await createReminder({
            note_id: note?.id ?? null,
            title: `${t('notes.title')}: ${title || t('notes.untitled')}`,
            body: content.slice(0, 200),
            time: reminderTime,
            days_of_week: reminderDays,
            enabled: reminderEnabled,
          });
          reminderId = r.id;
        }
      } else if (reminderId && linkedReminder) {
        await removeReminder(reminderId);
        reminderId = null;
      }

      await onSave({ title, content, color, tags, reminder_id: reminderId, updated_at: nowISO() });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={note ? t('notes.editor_edit') : t('notes.editor_new')}
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          {onDelete && note ? (
            <Button variant="danger" size="sm" onClick={onDelete}>
              <Trash2 size={14} /> {t('common.delete')}
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button loading={saving} onClick={handleSave} disabled={!title.trim()}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('notes.field_title')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('notes.field_title_ph')} autoFocus />
        </Field>

        <Field label={t('notes.field_content')}>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t('notes.field_content_ph')} rows={6} />
        </Field>

        <Field label={t('notes.field_color')}>
          <div className="flex flex-wrap gap-2">
            {NOTE_COLOR_KEYS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={cn(
                  'w-8 h-8 rounded-full border-2 transition-all',
                  NOTE_COLORS[c].dot,
                  color === c ? 'border-slate-900 dark:border-white scale-110' : 'border-transparent',
                )}
                title={NOTE_COLORS[c].label}
              />
            ))}
          </div>
        </Field>

        <Field label={t('notes.field_tags')}>
          <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder={t('notes.field_tags_ph')} />
        </Field>

        {/* Sezione PROMEMORIA */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowReminderSection((v) => !v)}
            className="w-full flex items-center justify-between p-3 bg-rose-50/50 dark:bg-rose-900/10 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
          >
            <div className="flex items-center gap-2">
              {showReminderSection ? <BellRing size={16} className="text-rose-500" /> : <Bell size={16} className="text-slate-400" />}
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('notes.reminder_section')}</span>
              {linkedReminder && (
                <Badge className="text-[10px] bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">{t('notes.reminder_active')}</Badge>
              )}
            </div>
            <span className="text-xs text-slate-400">{showReminderSection ? t('notes.reminder_remove') : t('notes.reminder_add')}</span>
          </button>

          {showReminderSection && (
            <div className="p-3 space-y-3 animate-slide-down">
              <Field label={t('notes.reminder_time')}>
                <Input type="time" value={reminderTime} onChange={(e) => setReminderTime(e.target.value)} />
              </Field>

              <div>
                <Label>{t('notes.reminder_days')}</Label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {presets.map((p) => {
                    const active = p.days.length === reminderDays.length && p.days.every((d) => reminderDays.includes(d));
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setReminderDays(p.days)}
                        className={cn(
                          'px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
                          active ? 'bg-brand-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
                        )}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {weekdays.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggleDay(d.value)}
                      className={cn(
                        'py-2 rounded-lg text-xs font-semibold transition-all',
                        reminderDays.includes(d.value) ? cn(d.color, 'text-white') : 'bg-slate-100 dark:bg-slate-800 text-slate-400',
                      )}
                    >
                      {d.short}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setReminderEnabled(!reminderEnabled)}
                className="w-full flex items-center justify-between p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60"
              >
                <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                  {reminderEnabled ? t('notes.reminder_enabled') : t('notes.reminder_disabled')}
                </span>
                <div className={cn('w-9 h-5 rounded-full relative transition-colors', reminderEnabled ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600')}>
                  <div className={cn('absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform', reminderEnabled && 'translate-x-4')} />
                </div>
              </button>

              <div className="flex items-start gap-1.5 text-[11px] text-slate-400">
                <Clock size={11} className="mt-0.5 shrink-0" />
                <p>{t('notes.reminder_hint')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
